import { Avatar as DiceBearAvatar } from "@dicebear/core";
import disco from "@dicebear/styles/disco.json" with { type: "json" };
import glass from "@dicebear/styles/glass.json" with { type: "json" };
import identicon from "@dicebear/styles/identicon.json" with { type: "json" };
import initials from "@dicebear/styles/initials.json" with { type: "json" };
import shapes from "@dicebear/styles/shapes.json" with { type: "json" };
import stripes from "@dicebear/styles/stripes.json" with { type: "json" };
import thumbs from "@dicebear/styles/thumbs.json" with { type: "json" };
import triangles from "@dicebear/styles/triangles.json" with { type: "json" };
import { isWeb } from "@memorang/helpers/platform";
import { useMemo } from "react";
import {
	type AvatarImageProps,
	Avatar as BaseAvatar,
} from "react-native-paper";
import { SvgXml } from "react-native-svg";
import { type AvatarType, useAppConfig } from "../contexts/AppConfigContext";
import Box from "./Box";

type Props = {
	type?: AvatarType;
	size?: number;
	username: string;
	style?: AvatarImageProps["style"];
};

const AVATAR_STYLE_BY_TYPE = {
	initials,
	disco,
	glass,
	identicon,
	shapes,
	stripes,
	thumbs,
	triangles,
} satisfies Record<AvatarType, unknown>;

export const Avatar = ({ type, size = 40, username, style }: Props) => {
	const { avatarType: configAvatarType } = useAppConfig();
	const resolvedType = type ?? configAvatarType ?? "initials";

	const avatarSvg = useMemo(
		() =>
			new DiceBearAvatar(AVATAR_STYLE_BY_TYPE[resolvedType], {
				seed: username,
				size: isWeb ? size * 2 : size,
			}),
		[resolvedType, size, username],
	);

	if (isWeb) {
		return (
			<BaseAvatar.Image
				source={{ uri: avatarSvg.toDataUri() }}
				size={size}
				style={{
					backgroundColor: "transparent",
					...style,
				}}
			/>
		);
	}

	return (
		<Box
			width={size}
			height={size}
			borderRadius={size / 2}
			overflow="hidden"
			style={style}
		>
			<SvgXml xml={avatarSvg.toString()} width={size} height={size} />
		</Box>
	);
};
